import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
// Native MUI Components
import { 
  Card, 
  CardContent, 
  CardHeader,
  Typography, 
  Button,
  Box,
  Stack,
  Alert,
  CircularProgress,
  Divider,
  Collapse,
  IconButton,
  Tabs,
  Tab
} from '@mui/material';
import { 
  Download, 
  Receipt, 
  Edit, 
  Check, 
  Timeline as TimelineIcon, 
  ExpandMore, 
  ExpandLess,
  Payment as PaymentIcon
} from '@mui/icons-material';
import { Bill, BillStatus, Trip } from '@/types';
import { billService } from '@/services/billService';
import { exportBillToPdf } from '@/services/pdfExportService';
import { useI18n } from '@/hooks/useI18n';
import { useAuth } from '@/contexts/AuthContext';
import BillStatusChip from '@/components/shared/BillStatusChip';
import StatusUpdateDialog from '@/components/shared/StatusUpdateDialog';
import PaymentUpdateDialog from '@/components/shared/PaymentUpdateDialog';
import BillTimeline from './BillTimeline';
import PaymentTransactionManager from './PaymentTransactionManager';

interface BillCardProps {
  bill: Bill;
  trip: Trip;
  onUpdate: () => void;
}

const BillCard: React.FC<BillCardProps> = ({ bill, trip, onUpdate }) => {
  const { tripId } = useParams<{ tripId: string }>();
  const { t } = useI18n();
  const { currentUser } = useAuth();
  const [tabValue, setTabValue] = useState(0);
  const [isStatusDialogOpen, setIsStatusDialogOpen] = useState(false);
  const [isPaymentDialogOpen, setIsPaymentDialogOpen] = useState(false);
  const [isTimelineExpanded, setIsTimelineExpanded] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleExportPdf = async () => {
    try {
      setIsLoading(true);
      await exportBillToPdf(bill, trip);
    } catch (error) {
      console.error('Error exporting PDF:', error);
      setError(t('bill.persistent.pdfExportError'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateStatus = async (newStatus: BillStatus) => {
    if (!tripId) return;
    
    try {
      setIsLoading(true);
      await billService.updateBillStatus(
        tripId, 
        bill.id, 
        newStatus,
        currentUser?.uid,
        currentUser?.displayName || currentUser?.email
      );
      setIsStatusDialogOpen(false);
      onUpdate();
    } catch (error) {
      console.error('Error updating status:', error);
      setError(t('bill.persistent.billGenerationError'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdatePayment = async (amount: number) => {
    if (!tripId) return;
    
    try {
      setIsLoading(true);
      const isPaid = amount >= bill.grandTotal;
      await billService.updatePaymentStatus(
        tripId, 
        bill.id, 
        isPaid, 
        amount,
        currentUser?.uid,
        currentUser?.displayName || currentUser?.email,
        'Manual Entry', // Default payment method
        undefined // No reference for manual entry
      );
      setIsPaymentDialogOpen(false);
      onUpdate();
    } catch (error) {
      console.error('Error updating payment:', error);
      setError(t('bill.persistent.billGenerationError'));
    } finally {
      setIsLoading(false);
    }
  };

  const remainingAmount = bill.grandTotal - bill.paidAmount;

  return (
    <>
      <Card>
        <CardHeader
          title={
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6">
                {bill.apartmentName}
              </Typography>
              <BillStatusChip status={bill.status} />
            </Box>
          }
          subheader={
            <Box>
              <Typography variant="body2" color="text.secondary">
                {t('bill.persistent.billId')}: {bill.id.slice(-6)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('bill.persistent.generatedAt')}: {bill.generatedAt.toLocaleDateString()}
              </Typography>
            </Box>
          }
          action={
            <Stack direction="row" spacing={1}>
              <Button
                size="small"
                startIcon={<Download />}
                onClick={handleExportPdf}
                disabled={isLoading}
              >
                {t('bill.persistent.exportPdf')}
              </Button>
              <Button
                size="small"
                startIcon={<Edit />}
                onClick={() => setIsStatusDialogOpen(true)}
                disabled={isLoading}
              >
                {t('bill.persistent.updateStatus')}
              </Button>
              <Button
                size="small"
                startIcon={<Check />}
                onClick={() => setIsPaymentDialogOpen(true)}
                disabled={isLoading}
                color={bill.status === BillStatus.FULLY_PAID ? 'success' : 'primary'}
              >
                {t('bill.persistent.updatePayment')}
              </Button>
            </Stack>
          }
        />
        <CardContent>
          {error && (
            <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {/* Tabs for organizing content */}
          <Tabs value={tabValue} onChange={(_, newValue) => setTabValue(newValue)} sx={{ mb: 2 }}>
            <Tab 
              icon={<Receipt />}
              label={t('bill.billDetails')} 
              iconPosition="start"
            />
            <Tab 
              icon={<PaymentIcon />}
              label={`${t('bill.payments')} (${bill.paymentTransactions?.length || 0})`}
              iconPosition="start"
            />
            <Tab 
              icon={<TimelineIcon />}
              label={t('bill.timeline')}
              iconPosition="start"
            />
          </Tabs>

          {/* Tab Content */}
          {tabValue === 0 && (
            <Stack spacing={2}>
              {/* Travellers List */}
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  {t('bill.persistent.travellers')}:
                </Typography>
                {bill.travellers.map((traveller, index) => (
                  <Box key={index} sx={{ ml: 2, mb: 1 }}>
                    <Typography variant="body2">
                      {traveller.name}: €{traveller.total.toFixed(2)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>
                      {t('apartmentType.accommodation')}: €{traveller.accommodationCost.toFixed(2)} | 
                      {t('bill.skiPass')}: €{traveller.skiPassCost.toFixed(2)} | 
                      {t('bill.transportation')}: €{traveller.transportationCost.toFixed(2)} | 
                      {t('bill.membership')}: €{traveller.membershipFee.toFixed(2)}
                    </Typography>
                  </Box>
                ))}
              </Box>

              <Divider />

              {/* Bill Summary */}
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  {t('bill.summary')}:
                </Typography>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2">{t('bill.totalAmount')}:</Typography>
                  <Typography variant="body2" fontWeight="bold">€{bill.grandTotal.toFixed(2)}</Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2">{t('bill.persistent.paidAmount')}:</Typography>
                  <Typography variant="body2" color={bill.paidAmount > 0 ? 'success.main' : 'text.secondary'}>
                    €{bill.paidAmount.toFixed(2)}
                  </Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2">{t('bill.persistent.remainingAmount')}:</Typography>
                  <Typography 
                    variant="body2" 
                    fontWeight="bold"
                    color={remainingAmount > 0 ? 'warning.main' : 'success.main'}
                  >
                    €{remainingAmount.toFixed(2)}
                  </Typography>
                </Stack>
              </Box>

              {/* Status Information */}
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  {t('bill.persistent.statusInfo')}:
                </Typography>
                {bill.sentAt && (
                  <Typography variant="body2" color="text.secondary">
                    {t('bill.persistent.sentAt')}: {bill.sentAt.toLocaleDateString()}
                  </Typography>
                )}
                {bill.paidAt && (
                  <Typography variant="body2" color="text.secondary">
                    {t('bill.persistent.paidAt')}: {bill.paidAt.toLocaleDateString()}
                  </Typography>
                )}
              </Box>
            </Stack>
          )}

          {/* Payment Transactions Tab */}
          {tabValue === 1 && tripId && (
            <PaymentTransactionManager
              tripId={tripId}
              billId={bill.id}
              transactions={bill.paymentTransactions || []}
              billGrandTotal={bill.grandTotal}
              currentPaidAmount={bill.paidAmount}
              onUpdate={onUpdate}
            />
          )}

          {/* Events Timeline Tab */}
          {tabValue === 2 && (
            <Box>
              {bill.events && bill.events.length > 0 ? (
                <BillTimeline events={bill.events} showDetails={true} />
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                  {t('bill.noEventsAvailable')}
                </Typography>
              )}
            </Box>
          )}

          {isLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              <CircularProgress />
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Reusable Dialogs */}
      <StatusUpdateDialog
        open={isStatusDialogOpen}
        onClose={() => setIsStatusDialogOpen(false)}
        onConfirm={handleUpdateStatus}
        currentStatus={bill.status}
        isLoading={isLoading}
        error={error}
      />

      <PaymentUpdateDialog
        open={isPaymentDialogOpen}
        onClose={() => setIsPaymentDialogOpen(false)}
        onConfirm={handleUpdatePayment}
        totalAmount={bill.grandTotal}
        currentPaidAmount={bill.paidAmount}
        isLoading={isLoading}
        error={error}
      />
    </>
  );
};

export default BillCard;
